import { isUsStateCode } from "../../utils/usStates.mjs";
import { isActiveAt } from "./consultant.mjs";
import { formatClinicalConsultantCustomerDisplayName } from "./clinicalConsultantDisplayName.mjs";

// src/models/consultation/clinicalAvailability.ts
/** Per-template clinical availability cache docs under `tellescope_availability_slots`. */
const CLINICAL_AVAILABILITY_SLOTS_MAIN_DOC_ID = "availability_slots_clinical_main";
const CLINICAL_AVAILABILITY_SLOTS_FOLLOWUP_DOC_ID = "availability_slots_clinical_followup";
/**
* @deprecated Prefer {@link clinicalAvailabilityDocIdForConsultType}.
* Legacy unified doc — migration reads only; do not write.
*/
const CLINICAL_AVAILABILITY_SLOTS_DOC_ID = "clinical_availability_slots";
/** @deprecated Prefer {@link clinicalAvailabilityDocIdForConsultType}. Kept for migration reads. */
const CLINICAL_AVAILABILITY_DOC_PREFIX = "clinical_";
/** Maximum on-demand clinical booking horizon (product limit). */
const CLINICAL_BOOKING_HORIZON_WEEKS = 4;
/** Admin follow-up booking horizon (tomorrow NY → +8 weeks). */
const CLINICAL_ADMIN_FOLLOWUP_BOOKING_HORIZON_WEEKS = 8;
/** Calendar bound for admin follow-up earliest bookable day. */
const CLINICAL_ADMIN_FOLLOWUP_BOOKING_BOUND_ZONE = "America/New_York";
/** Firestore doc id for the clinical availability cache for a consult type. */
function clinicalAvailabilityDocIdForConsultType(consultType) {
	switch (consultType) {
		case "clinical_followup": return CLINICAL_AVAILABILITY_SLOTS_FOLLOWUP_DOC_ID;
		case "clinical_main": return CLINICAL_AVAILABILITY_SLOTS_MAIN_DOC_ID;
		default: throw new Error(`Unhandled consult type: ${consultType}`);
	}
}
/** Firestore doc id for legacy per-state clinical availability cache. */
function clinicalAvailabilityDocId(stateCode) {
	const normalized = stateCode.trim().toUpperCase();
	if (!isUsStateCode(normalized)) throw new Error(`Invalid US state code: ${stateCode}`);
	return `${CLINICAL_AVAILABILITY_DOC_PREFIX}${normalized}`;
}
/** Shared roster **R** for self-pay and insured clinical booking gates. */
function isConsultantBookableForClinicalSlot(consultant, at, stateCode) {
	if (!consultant.approvedForConsults) return false;
	if (!isActiveAt(consultant, at)) return false;
	if (!consultant.bridgeProviderId || !consultant.canvasPractitionerId) return false;
	if (!consultant.tellescopeUserId) return false;
	if (stateCode && !consultant.manualLicensedStates?.includes(stateCode)) return false;
	return true;
}
/**
* Builds Tellescope `userIds[]` for on-demand clinical availability.
* See ADR-003 / pad H5 step 2 — four-way link + licensure + active roster gates.
*/
function buildClinicalUserIds(consultants, options = {}) {
	const stateCode = options.stateCode?.trim().toUpperCase();
	if (stateCode && !isUsStateCode(stateCode)) throw new Error(`Invalid US state code: ${options.stateCode}`);
	const at = options.at ?? /* @__PURE__ */ new Date();
	const userIds = /* @__PURE__ */ new Set();
	for (const consultant of consultants) {
		if (!isConsultantBookableForClinicalSlot(consultant, at, stateCode)) continue;
		const userId = consultant.tellescopeUserId;
		if (!userId) continue;
		userIds.add(userId);
	}
	return [...userIds].sort();
}
/**
* Map Tellescope userId → licensed US state codes for bookable clinical consultants.
* Used to filter the unified availability list by patient state of residence.
*/
function buildClinicalUserLicensedStateCodes(consultants, options = {}) {
	const at = options.at ?? /* @__PURE__ */ new Date();
	const map = {};
	for (const consultant of consultants) {
		if (!isConsultantBookableForClinicalSlot(consultant, at)) continue;
		const userId = consultant.tellescopeUserId;
		if (!userId) continue;
		const states = (consultant.manualLicensedStates ?? []).map((s) => s.trim().toUpperCase()).filter(isUsStateCode).sort();
		const existing = map[userId] ?? [];
		map[userId] = [.../* @__PURE__ */ new Set([...existing, ...states])].sort();
	}
	return map;
}
/** Consultant id lookup by Tellescope user id for bookable clinical NPs. */
function buildClinicalUserIdToConsultantId(consultants, options = {}) {
	const at = options.at ?? /* @__PURE__ */ new Date();
	const map = {};
	for (const consultant of consultants) {
		if (!isConsultantBookableForClinicalSlot(consultant, at)) continue;
		const userId = consultant.tellescopeUserId;
		if (!userId) continue;
		if (!map[userId]) map[userId] = consultant.id;
	}
	return map;
}
/** Public display name lookup by Tellescope user id for bookable clinical NPs. */
function buildClinicalUserIdToConsultantDisplayName(consultants, options = {}) {
	const at = options.at ?? /* @__PURE__ */ new Date();
	const map = {};
	for (const consultant of consultants) {
		if (!isConsultantBookableForClinicalSlot(consultant, at)) continue;
		const userId = consultant.tellescopeUserId;
		if (!userId) continue;
		if (map[userId]) continue;
		const displayName = formatClinicalConsultantCustomerDisplayName({
			firstName: consultant.firstName,
			lastName: consultant.lastName
		});
		if (displayName) map[userId] = displayName;
	}
	return map;
}

export { CLINICAL_ADMIN_FOLLOWUP_BOOKING_BOUND_ZONE, CLINICAL_ADMIN_FOLLOWUP_BOOKING_HORIZON_WEEKS, CLINICAL_AVAILABILITY_DOC_PREFIX, CLINICAL_AVAILABILITY_SLOTS_DOC_ID, CLINICAL_AVAILABILITY_SLOTS_FOLLOWUP_DOC_ID, CLINICAL_AVAILABILITY_SLOTS_MAIN_DOC_ID, CLINICAL_BOOKING_HORIZON_WEEKS, buildClinicalUserIdToConsultantDisplayName, buildClinicalUserIdToConsultantId, buildClinicalUserIds, buildClinicalUserLicensedStateCodes, clinicalAvailabilityDocId, clinicalAvailabilityDocIdForConsultType, isConsultantBookableForClinicalSlot };
//# sourceMappingURL=clinicalAvailability.mjs.map